// combat-pick.ts — GM "pick the aggressor" mode for Roll Initiative (owner 2026-08-26).
//
// Flow: Roll Initiative → this mode arms → the GM clicks a creature (FT3D standee or combat-strip chip; both
// funnel through the sidelog target selection and its `ft:targets-changed` event) → the browser
// previews the perception-grown combat island from retained encounter + FT3D truth → every joiner
// is highlighted on the map + listed in a floating panel with WHY it joins
// (seed / sight / hearing / summon / lair) → Start combat asks the browser combat lane to save that island.
//
import { claimFt3dInteractionMode, releaseFt3dInteractionMode } from '../shared/ft3d-interaction-mode';

// Highlight: FT3D via the existing `ft3d-campaign-target-state` iframe message with tone
// 'combat-island' — target ring + carets in each joiner's allegiance colour, the seed gold (owner
// 2026-09-10; a plain message is the red selection ring). my_campaign.php re-sends the plain
// selection on every `ft:targets-changed`, so we re-assert the island after each one while the
// preview is live.
// Panel CSS is injected once — self-contained, no css/src round-trip.

import {
    resolveClientCombatIsland,
    type ClientCombatTruth,
} from './client-combat-membership';
import { bucketCombatPick, type CombatPickTeam } from './combat-pick-buckets';
import { COMBAT_ALERT_HEARING_RADIUS_FT } from '../shared/combat-alert-radius';
import { refuseUnported } from '../janus-engine/unported';
import { withCombatProgress, refuseCombatChange, type CombatProgress } from '../shared/combat-settlement';

interface IslandParticipant {
    encounter_creature_id: string;
    name: string;
    item_type: string;
    team: string;
    /** Summoner's encounter_creature_id; non-empty marks a summon (it never rolls its own initiative). */
    controller_encounter_creature_id: string;
    reason: 'seed' | 'sight' | 'hearing' | 'summon' | 'lair' | string;
    private: boolean;
    /** Server baseline for the chip; the XZ widget's model overlays it when present. */
    pl?: number;
    creature_number?: number;
}

export interface CombatPickOptions {
    initialSeedId?: string;
    /** Join this exact active combat; existing members are never removable. */
    combatId?: string;
    /** Called with the seed and GM-curated exclusions when the GM confirms. */
    onStart: (seedIds: string[], excludedIds: string[], signal: AbortSignal, progress: CombatProgress) => Promise<'handled' | null | void>;
}

const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
const PANEL_ID = 'ft_combat_pick_panel';
const STYLE_ID = 'ft_combat_pick_style';

const REASON_LABEL: Record<string, { icon: string; text: string }> = {
    seed:    { icon: '⚔️', text: 'starts the fight' },
    sight:   { icon: '👁️', text: 'sees the fight' },
    hearing: { icon: '👂', text: 'hears the fight' },
    summon:  { icon: '🔗', text: 'follows its summoner' },
    lair:    { icon: '🏰', text: 'the lair itself' },
    manual:  { icon: '+', text: 'added by the GM' },
};

/** Explains the FT3D alert rings (preview3d refreshCampaignCombatAlertRings) drawn around the joiners. */
const ALERT_LEGEND = `<div class="ftcp-legend" data-ftcp-legend="alert-ring"><span class="ftcp-ring" aria-hidden="true"></span>Rings show the ${COMBAT_ALERT_HEARING_RADIUS_FT} ft hearing range (halved through closed doors) — opposing creatures inside one hear the fight and join; anyone who can see it joins from farther.</div>`;

const TEAM_LABEL: Record<CombatPickTeam, string> = {
    friend:  'Allies',
    enemy:   'Hostile',
    neutral: 'Neutral',
};

/** Chip model resolved from the retained encounter cache; final commit is revalidated by Janus. */
interface ChipModel { picUrl: string; creatureNumber: number; isPlayer: boolean; isLair: boolean; name: string; pl: number }

interface CachedEncounterRow {
    encounter_creatures?: unknown;
    computed?: { pl?: unknown };
}

function cachedEncounter(): { encounter_creatures_arr?: unknown[] } | null {
    const host = window as unknown as {
        ftGetActiveEncounterId?: () => unknown;
        FateTide?: { encounterCache?: Map<string, unknown> };
    };
    const encounterId = String(host.ftGetActiveEncounterId?.() || '');
    return (host.FateTide?.encounterCache instanceof Map
        ? host.FateTide.encounterCache.get(encounterId) as { encounter_creatures_arr?: unknown[] } | undefined
        : null) ?? null;
}

function cachedEncounterRow(encounterCreatureId: string): CachedEncounterRow | null {
    const rows = cachedEncounter()?.encounter_creatures_arr ?? [];
    return rows.find((candidate) => String(ftEncounterCreatureField(
        (candidate as CachedEncounterRow)?.encounter_creatures,
        'encounter_creature_id',
    ) ?? '').toUpperCase() === encounterCreatureId.toUpperCase()) as CachedEncounterRow | undefined ?? null;
}

function chipModel(p: IslandParticipant): ChipModel {
    const row = cachedEncounterRow(p.encounter_creature_id);
    let picUrl = '';
    if (row) {
        try {
            picUrl = String(pictureField0(selectCreaturePicture(row, { use_picture_thumbnail: true }), 'pic_url') ?? '');
        } catch (error) {
            ftDebugLog('combat', { phase: 'combatPickPictureError', encounterCreatureId: p.encounter_creature_id, error: String(error) });
        }
    }
    return {
        picUrl,
        creatureNumber: Number(p.creature_number ?? (row ? ftEncounterCreatureField(row.encounter_creatures, 'creature_number') : 0) ?? 0) || 0,
        isPlayer: p.item_type === 'player_character',
        isLair: p.item_type === 'lair',
        name: p.name,
        pl: Number(row?.computed?.pl ?? p.pl ?? 0) || 0,
    };
}

/** Participant chip resolved from the already-loaded encounter cache. */
function cachedParticipant(encounterCreatureId: string, reason = 'seed'): IslandParticipant | null {
    const row = cachedEncounterRow(encounterCreatureId);
    if (!row) return null;
    const itemType = String(ftItemsCol(row, 'item_type') ?? ftEncounterCreatureField(row.encounter_creatures, 'item_type') ?? '');
    return {
        encounter_creature_id: encounterCreatureId.toUpperCase(),
        name: String(getCreatureName(row) || 'Creature'),
        item_type: itemType,
        team: String(ftEncounterCreatureField(row.encounter_creatures, 'team') ?? ''),
        controller_encounter_creature_id: String(ftEncounterCreatureField(row.encounter_creatures, 'controller_encounter_creature_id') ?? '').trim().toUpperCase(),
        reason,
        private: Number(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_private') ?? 0) === 1,
        pl: Number(row.computed?.pl ?? 0) || 0,
        creature_number: Number(ftEncounterCreatureField(row.encounter_creatures, 'creature_number') ?? 0) || 0,
    };
}

let active: {
    options: CombatPickOptions;
    seedIds: string[];
    participants: IslandParticipant[];
    /** Non-seed rows the GM explicitly removed from this proposed combat island. */
    excludedIds: Set<string>;
    knownSelection: Set<string>;
    previewToken: number;
    busy: boolean;
    pending?: AbortController;
    progress?: string;
    loading: boolean;
    adding: boolean;
} | null = null;

function selectionIds(): string[] {
    const raw = String(window.ftGetTargetSelection?.() ?? '').trim();
    if (!raw) return [];
    return raw.split(',').map((part) => part.trim().toUpperCase()).filter((part) => UUID_RE.test(part));
}

function ft3dFrame(): HTMLIFrameElement | null {
    return document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
}

function combatPreviewTruth(): ClientCombatTruth | null {
    const frameWindow = ft3dFrame()?.contentWindow as (Window & {
        ft3dGetCombatPreviewTruth?: () => unknown;
    }) | null | undefined;
    const raw = frameWindow?.ft3dGetCombatPreviewTruth?.() as {
        schema?: unknown;
        encounterId?: unknown;
        shape?: unknown;
        lightGrid?: unknown;
    } | null | undefined;
    const activeEncounterId = String((window as Window & {
        ftGetActiveEncounterId?: () => unknown;
    }).ftGetActiveEncounterId?.() ?? '').trim().toUpperCase();
    if (raw?.schema !== 'ft.ft3d.combat-preview-truth.v1'
        || (activeEncounterId && String(raw.encounterId ?? '').trim().toUpperCase() !== activeEncounterId)) return null;
    const shape = raw.shape as ClientCombatTruth['shape'] | undefined;
    if (!shape || !Array.isArray(shape.boxes)) return null;
    return { shape, lightGrid: raw.lightGrid as ClientCombatTruth['lightGrid'] };
}

function postPickMode(active: boolean): void {
    const frame = ft3dFrame();
    if (!frame?.contentWindow) return;
    frame.contentWindow.postMessage({ type: 'ft3d-campaign-creature-pick-mode-v1', payload: { active } }, window.location.origin);
}

// The FT3D iframe reports a single-click creature pick here (its pick mode suppresses the card pin
// and paints the hover ring; see preview3d.ts campaignCreaturePickMode).
function onFrameMessage(event: MessageEvent): void {
    if (!active || event.origin !== window.location.origin || event.source !== ft3dFrame()?.contentWindow) return;
    const envelope = event.data as { type?: unknown; payload?: { entityId?: unknown } } | null;
    if (envelope?.type !== 'ft3d-campaign-creature-pick-v1') return;
    const entityId = String(envelope.payload?.entityId ?? '').trim().toUpperCase();
    if (!UUID_RE.test(entityId)) return;
    pickCombatCreature(entityId);
}

/** `island` paints the joiners in their allegiance colour (seed gold); omit it for the plain red selection. */
function postTargetState(entityIds: string[], island?: { seedIds: string[] }): void {
    const frame = ft3dFrame();
    if (!frame?.contentWindow) return;
    const payload = island ? { entityIds, tone: 'combat-island', seedIds: island.seedIds } : { entityIds };
    frame.contentWindow.postMessage({ type: 'ft3d-campaign-target-state', payload }, window.location.origin);
}

function ensureStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
#${PANEL_ID} {
    position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%);
    z-index: 2147483000; min-width: 320px; max-width: min(560px, 92vw);
    background: rgba(6, 26, 30, 0.96); color: #dff6f2; border: 1px solid #1f8f86;
    border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,.55); font-size: 13px;
    backdrop-filter: blur(6px);
}
#${PANEL_ID} .ftcp-head { display:flex; align-items:center; gap:8px; padding:10px 12px; border-bottom:1px solid rgba(31,143,134,.5); font-weight:600; }
#${PANEL_ID} .ftcp-head .ftcp-title { flex:1; }
#${PANEL_ID} .ftcp-close { margin:-4px -5px -4px 0; }
#${PANEL_ID} .ftcp-body { max-height: 40vh; overflow:auto; padding: 10px 12px; }
#${PANEL_ID} .ftcp-hint { padding: 12px; color:#9fd9d0; }
#${PANEL_ID} .ftcp-body { display:flex; flex-direction:column; gap:8px; }
#${PANEL_ID} .ftcp-bucket + .ftcp-bucket { border-top:1px solid rgba(31,143,134,.35); padding-top:8px; }
#${PANEL_ID} .ftcp-bucket-label, #${PANEL_ID} .ftcp-summons-label { font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; margin-bottom:6px; }
#${PANEL_ID} .ftcp-bucket-friend .ftcp-bucket-label { color:#7ee0d4; }
#${PANEL_ID} .ftcp-bucket-enemy .ftcp-bucket-label { color:#ff8a8a; }
#${PANEL_ID} .ftcp-bucket-neutral .ftcp-bucket-label { color:#e6d9a8; }
#${PANEL_ID} .ftcp-summons { margin-top:8px; padding-left:10px; border-left:2px dashed rgba(255,255,255,.18); }
#${PANEL_ID} .ftcp-summons-label { color:#9fb8b4; font-weight:600; }
#${PANEL_ID} .ftcp-chips { display:flex; flex-wrap:wrap; gap:10px 8px; align-content:flex-start; }
#${PANEL_ID} .ftcp-row { position:relative; width:52px; display:flex; flex-direction:column; align-items:center; gap:3px; cursor:default; }
#${PANEL_ID} .ftcp-chip { position:relative; width:40px; height:40px; border-radius:50%; border:3px solid #ff3b3b; background:#2a1416; box-sizing:content-box; }
#${PANEL_ID} .ftcp-chip img, #${PANEL_ID} .ftcp-chip .ftcp-initials { width:100%; height:100%; border-radius:50%; object-fit:contain; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px; color:#fff; }
#${PANEL_ID} .ftcp-chip .ftcp-initials { background:#8b1e1e; }
#${PANEL_ID} .ftcp-chip .ftcp-lair-icon { width:100%; height:100%; border-radius:50%; display:flex; align-items:center; justify-content:center; background:#1d2a2a; }
#${PANEL_ID} .ftcp-chip .ftcp-lair-icon img, #${PANEL_ID} .ftcp-chip .ftcp-lair-icon svg { width:28px; height:28px; border-radius:0; }
#${PANEL_ID} .ftcp-row.ftcp-team-friend .ftcp-chip { border-color:#7ee0d4; background:#0f2b28; }
#${PANEL_ID} .ftcp-row.ftcp-team-friend .ftcp-chip .ftcp-initials { background:#1f8f86; }
#${PANEL_ID} .ftcp-row.ftcp-team-neutral .ftcp-chip { border-color:#c9b37a; background:#2a2616; }
#${PANEL_ID} .ftcp-row.ftcp-team-neutral .ftcp-chip .ftcp-initials { background:#6f6134; }
#${PANEL_ID} .ftcp-row.ftcp-summon .ftcp-chip { width:34px; height:34px; border-style:dashed; }
#${PANEL_ID} .ftcp-row.ftcp-seed .ftcp-chip { border-color:#ffd166; box-shadow:0 0 0 2px rgba(255,209,102,.35), 0 0 12px rgba(255,209,102,.5); }
#${PANEL_ID} .ftcp-num { position:absolute; top:-6px; left:-6px; min-width:18px; height:18px; padding:0 4px; border-radius:9px; background:#111; color:#fff; border:1px solid #ddd; font-size:11px; line-height:16px; text-align:center; font-weight:700; }
#${PANEL_ID} .ftcp-why { position:absolute; right:-8px; bottom:-6px; width:20px; height:20px; border-radius:50%; background:rgba(6,26,30,.95); border:1px solid rgba(255,255,255,.25); font-size:12px; line-height:18px; text-align:center; }
#${PANEL_ID} .ftcp-name { width:100%; text-align:center; font-size:10px; line-height:1.1; color:#cfeee8; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
#${PANEL_ID} .ftcp-row.ftcp-team-friend .ftcp-name { color:#7ee0d4; }
#${PANEL_ID} .ftcp-row.ftcp-team-neutral .ftcp-name { color:#e6d9a8; }
#${PANEL_ID} .ftcp-row.ftcp-seed .ftcp-name { color:#ffd166; font-weight:600; }
#${PANEL_ID} .ftcp-remove { position:absolute; top:-7px; right:-5px; z-index:2; width:20px; height:20px; padding:0; border-radius:50%; border:1px solid rgba(255,255,255,.75); background:#411619; color:#fff; font-size:14px; line-height:17px; text-align:center; cursor:pointer; }
#${PANEL_ID} .ftcp-remove:hover, #${PANEL_ID} .ftcp-remove:focus-visible { background:#a5232c; outline:2px solid #fff; outline-offset:1px; }
#${PANEL_ID} .ftcp-foot { display:flex; gap:8px; justify-content:flex-end; padding:10px 12px; border-top:1px solid rgba(31,143,134,.5); }
#${PANEL_ID} .ftcp-count { background:#1f8f86; color:#06201d; border-radius:10px; padding:1px 8px; font-size:12px; }
#${PANEL_ID} .ftcp-legend { padding:0 12px 8px; font-size:11px; color:#9fb8b4; }
#${PANEL_ID} .ftcp-legend .ftcp-ring { display:inline-block; width:10px; height:10px; margin-right:5px; vertical-align:-1px; border-radius:50%; border:2px solid #ffd166; }
`;
    document.head.appendChild(style);
}

function panel(): HTMLElement {
    ensureStyle();
    let el = document.getElementById(PANEL_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = PANEL_ID;
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-label', 'Start combat');
        document.body.appendChild(el);
        el.addEventListener('click', (event) => {
            const target = (event.target as HTMLElement).closest<HTMLElement>('[data-ftcp-action]');
            if (!target) return;
            const action = target.dataset.ftcpAction;
            if (action === 'cancel') cancelCombatPick();
            if (action === 'start') void confirmCombatPick();
            if (action === 'exclude') void excludeParticipant(String(target.dataset.ecid ?? ''));
            if (action === 'restore') void restoreExcludedParticipants();
            if (action === 'add-mode' && active && !active.busy && !active.loading) {
                active.adding = !active.adding;
                render();
            }
            if (action === 'include') pickCombatCreature(String(target.dataset.ecid ?? ''));
        });
    }
    return el;
}

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
}

function render(): void {
    if (!active) return;
    const el = panel();
    const { participants, seedIds, excludedIds, busy, loading } = active;
    const joining = !!active.options.combatId;
    el.setAttribute('aria-label', joining ? 'Add to combat' : 'Start combat');
    el.dataset.state = seedIds.length === 0 ? 'armed' : loading ? 'loading' : 'ready';
    const seedRow = participants.find((p) => seedIds.includes(p.encounter_creature_id));
    const cancelIcon = typeof iconMap === 'function' ? iconMap('close', 18, 18, false) : '×';
    const startIcon = typeof iconMap === 'function' ? iconMap('roll_initiative', 18, 18, false) : '⚔️';
    const closeButton = `<button type="button" class="ft-window-close ftcp-close" data-ftcp-action="cancel" aria-label="Close initiative picker" title="Close" ${busy ? 'disabled' : ''}>×</button>`;

    if (seedIds.length === 0 && !joining) {
        el.innerHTML = `
            <div class="ftcp-head"><span class="ftcp-title">⚔️ Start combat</span>${closeButton}</div>
            <div class="ftcp-hint">Click the creature that starts the fight — it turns aggressive and everyone who can see or hear it joins. <kbd>Esc</kbd> cancels.</div>
            ${ALERT_LEGEND}
            <div class="ftcp-foot ft-window-actions"><button type="button" class="btn btn-sm btn-outline-light ft-window-action ft-window-action--cancel" data-ftcp-action="cancel"><span class="ft-window-action__content">${cancelIcon}<span>Cancel</span></span></button></div>`;
        return;
    }

    // Icon strip, like the combat-widget bar (owner 2026-08-27), bucketed by TEAM TYPE (owner
    // 2026-09-10): Allies (teal) → Hostile (red) → Neutral (sand), each with its main actors first —
    // player characters, then NPCs from highest PL to lowest (lairs sink by their PL) — and its
    // summons in a dashed sub-group below them, the way the server appends summons to the bottom of
    // the initiative order. The seed glows gold; the WHY is a corner badge (⚔️ / 👁️ / 👂 / 🔗 / 🏰).
    const models = participants.map((p) => {
        const m = chipModel(p);
        return { p, m, id: p.encounter_creature_id, team: p.team, controllerId: p.controller_encounter_creature_id, isPlayer: m.isPlayer, name: m.name, pl: m.pl };
    });
    const nameById = new Map(models.map((x) => [x.id.toUpperCase(), x.name]));
    const chip = ({ p, m }: { p: IslandParticipant; m: ChipModel }, team: CombatPickTeam): string => {
        const why = REASON_LABEL[p.reason] ?? { icon: '•', text: p.reason };
        const isSummon = p.controller_encounter_creature_id !== '';
        const cls = ['ftcp-row', `ftcp-team-${team}`, p.reason === 'seed' ? 'ftcp-seed' : '', isSummon ? 'ftcp-summon' : ''].filter(Boolean).join(' ');
        const initials = m.name ? m.name.charAt(0).toUpperCase() + (m.name.length > 1 ? m.name.charAt(1).toLowerCase() : '') : '??';
        const lairIcon = typeof iconMap === 'function' ? iconMap('lair', 28, 28, false) : '';
        const face = m.isLair
            ? `<div class="ftcp-lair-icon">${lairIcon || '<span class="ftcp-initials">L</span>'}</div>`
            : m.picUrl
                ? `<img src="${escapeHtml(m.picUrl)}" alt="" draggable="false">`
                : `<div class="ftcp-initials">${escapeHtml(initials)}</div>`;
        const num = !m.isPlayer && !m.isLair && m.creatureNumber > 0 ? `<span class="ftcp-num">${m.creatureNumber}</span>` : '';
        const summoner = isSummon ? nameById.get(p.controller_encounter_creature_id) : undefined;
        const title = `${m.name} — ${why.text}${summoner ? ` · summoned by ${summoner}` : ''}${m.pl > 0 ? ` · PL ${Math.round(m.pl)}` : ''}`;
        const remove = joining || p.encounter_creature_id !== seedIds[0]
            ? `<button type="button" class="ftcp-remove" data-ftcp-action="exclude" data-ecid="${escapeHtml(p.encounter_creature_id)}" aria-label="Remove ${escapeHtml(m.name)} from this combat" title="Remove from this combat" ${busy || loading ? 'disabled' : ''}>×</button>`
            : '';
        return `<div class="${cls}" data-ecid="${escapeHtml(p.encounter_creature_id)}" data-reason="${escapeHtml(p.reason)}" data-team="${team}" data-summon="${isSummon ? '1' : '0'}" title="${escapeHtml(title)}">
            ${remove}<div class="ftcp-chip">${face}${num}<span class="ftcp-why" aria-label="${escapeHtml(why.text)}">${why.icon}</span></div>
            <span class="ftcp-name">${escapeHtml(m.name)}</span>
        </div>`;
    };
    const rows = bucketCombatPick(models).map(({ team, actors, summons }) => {
        const count = actors.length + summons.length;
        const actorChips = actors.length > 0 ? `<div class="ftcp-chips">${actors.map((x) => chip(x, team)).join('')}</div>` : '';
        const summonChips = summons.length > 0
            ? `<div class="ftcp-summons"><div class="ftcp-summons-label">🔗 Summons</div><div class="ftcp-chips">${summons.map((x) => chip(x, team)).join('')}</div></div>`
            : '';
        return `<div class="ftcp-bucket ftcp-bucket-${team}" data-team="${team}">
            <div class="ftcp-bucket-label">${TEAM_LABEL[team]} · ${count}</div>${actorChips}${summonChips}
        </div>`;
    }).join('');

    const candidates = active.adding ? (cachedEncounter()?.encounter_creatures_arr ?? [])
        .map(row => String(ftEncounterCreatureField((row as CachedEncounterRow).encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase())
        .filter(id => UUID_RE.test(id) && !participants.some(p => p.encounter_creature_id === id))
        .map(id => cachedParticipant(id))
        .filter((p): p is IslandParticipant => p !== null && eligiblePick(p.encounter_creature_id)) : [];
    const additions = active.adding ? `<div class="ftcp-hint">Click creatures in the Scene or choose below. Creatures that notice them may also join.${joining ? ' Existing initiative and the current turn are preserved.' : ''}</div>
        <div class="ftcp-body">${candidates.map(p => `<button type="button" class="btn btn-sm btn-outline-light" data-ftcp-action="include" data-ecid="${escapeHtml(p.encounter_creature_id)}">Add ${escapeHtml(p.name)}${p.creature_number ? ` #${p.creature_number}` : ''}</button>`).join('') || 'No other eligible creatures.'}</div>` : '';
    el.innerHTML = `
        <div class="ftcp-head">
            <span class="ftcp-title">⚔️ ${joining ? 'Add to combat' : escapeHtml(seedRow?.name ?? 'Combat')}</span>
            <span class="ftcp-count">${loading ? 'Checking…' : `${participants.length} join${excludedIds.size > 0 ? ` · ${excludedIds.size} removed` : ''}`}</span>
            ${closeButton}
        </div>
        <div class="ftcp-body">${rows || `<div class="ftcp-hint">${loading ? 'Working out who notices…' : 'Nobody would join.'}</div>`}</div>
        ${ALERT_LEGEND}
        ${additions}
        <div class="ftcp-foot ft-window-actions">
            ${joining ? '' : `<button type="button" class="btn btn-sm btn-outline-light ft-window-action" data-ftcp-action="add-mode" aria-pressed="${active.adding}" ${busy || loading ? 'disabled' : ''}>${active.adding ? 'Done adding' : 'Add creatures'}</button>`}
            ${excludedIds.size > 0 ? `<button type="button" class="btn btn-sm btn-outline-light ft-window-action" data-ftcp-action="restore" ${busy || loading ? 'disabled' : ''}>Restore removed</button>` : ''}
            <button type="button" class="btn btn-sm btn-outline-light ft-window-action ft-window-action--cancel" data-ftcp-action="cancel" ${busy ? 'disabled' : ''}><span class="ft-window-action__content">${cancelIcon}<span>Cancel</span></span></button>
            <button type="button" class="btn btn-sm btn-danger ft-window-action" data-ftcp-action="start" ${busy || loading || participants.length === 0 ? 'disabled' : ''}><span class="ft-window-action__content">${startIcon}<span>${joining ? (busy ? escapeHtml(active.progress ?? 'Preparing combat…') : 'Add to combat') : (busy ? escapeHtml(active.progress ?? 'Preparing combat…') : 'Start combat')}</span></span></button>
        </div>`;
}

function highlight(): void {
    if (!active) return;
    const ids = new Set(active.participants.map((p) => p.encounter_creature_id.toUpperCase()));
    postTargetState([...ids], { seedIds: active.options.combatId ? [] : active.seedIds.slice(0, 1) });
}

/** A named engine refusal is not a loading state: surface its reason instead of "not ready yet". Perception refuses
 *  `inventory-base-unavailable` for THIS encounter's floor, so that one is refined to the version fence or the floor's
 *  own refusal stage; any other reason is kept as thrown. Null for a non-refusal error. */
function previewRefusal(error: unknown): string | null {
    const message = error instanceof Error ? error.message : String(error ?? '');
    if (!message.startsWith('unported:')) return null;
    const reason = message.slice('unported:'.length);
    if (reason !== 'inventory-base-unavailable') return reason;
    if (window.ftBaseCommitsPaused?.()) return 'engine-version-reload';
    const floor = (cachedEncounter() as { _inventory_refusal?: unknown } | null)?._inventory_refusal;
    return typeof floor === 'string' ? floor.replace(/^unported:/, '') : reason;
}

async function preview(seedIds: string[], preserveExclusions = false): Promise<void> {
    if (!active) return;
    if (!preserveExclusions) active.excludedIds.clear();
    const token = ++active.previewToken;
    active.seedIds = seedIds;
    // Paint the chosen aggressor from the already-loaded browser cache immediately. The exact
    // sight/hearing/summon/lair closure replaces this provisional row from retained truth in the
    // same event turn. For a removal/revert, retain unaffected chips during recalculation.
    active.participants = preserveExclusions
        ? active.participants.filter((p) => !active!.excludedIds.has(p.encounter_creature_id.toUpperCase()))
        : seedIds.map(id => cachedParticipant(id)).filter((p): p is IslandParticipant => p !== null);
    active.loading = true;
    render();
    highlight();
    const startedAt = performance.now();
    try {
        const encounter = cachedEncounter();
        const truth = combatPreviewTruth();
        if (!encounter || !truth) throw new Error('Retained FT3D encounter truth is not ready');
        const existing = existingCombatMemberIds();
        const resolution = resolveClientCombatIsland(encounter as Record<string, unknown>, truth, [...existing, ...seedIds], [...active.excludedIds]);
        if (!active || token !== active.previewToken) return;
        active.participants = resolution.participantIds
            .filter(id => !existing.includes(id))
            .map(id => cachedParticipant(id, active!.seedIds.slice(active!.options.combatId ? 0 : 1).includes(id) ? 'manual' : resolution.reasons[id] ?? 'sight'))
            .filter((participant): participant is IslandParticipant => participant !== null);
        active.loading = false;
        render();
        highlight();
        ftDebugLog('combat', {
            phase: 'combatPickLocalPreview',
            source: 'client-encounter-truth',
            durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
            participantCount: active.participants.length,
        });
    } catch (error) {
        if (active && token === active.previewToken) active.participants = [];
        ftDebugLog('combat', { phase: 'combatPickPreviewError', error: String(error) });
        const refusal = previewRefusal(error);
        if (refusal) refuseUnported(refusal, { surface: 'initiative-pick',
            encounterId: String((window as Window & { ftGetActiveEncounterId?: () => unknown }).ftGetActiveEncounterId?.() ?? '') });
        else showUserMessage('FT3D encounter truth is not ready yet.');
    } finally {
        if (active && token === active.previewToken) { active.loading = false; render(); }
    }
}

async function excludeParticipant(encounterCreatureId: string): Promise<void> {
    if (!active || active.busy || active.loading) return;
    const id = encounterCreatureId.trim().toUpperCase();
    if (!UUID_RE.test(id) || (!active.options.combatId && active.seedIds[0] === id)) return;
    active.excludedIds.add(id);
    await preview([...active.seedIds], true);
}

async function restoreExcludedParticipants(): Promise<void> {
    if (!active || active.busy || active.loading || active.excludedIds.size === 0) return;
    active.excludedIds.clear();
    await preview([...active.seedIds], true);
}

function onTargetsChanged(): void {
    if (!active) return;
    const now = selectionIds();
    const added = now.filter((id) => !active!.knownSelection.has(id));
    active.knownSelection = new Set(now);
    if (added.length > 0) {
        // The newest pick is the aggressor. (Clicking a creature both selects it and seeds from it.)
        pickCombatCreature(added[added.length - 1] as string);
        return;
    }
    // Selection changed without a new creature (a deselect / my_campaign re-sync): re-assert the island ring.
    if (active.participants.length > 0) highlight();
}

function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && active) {
        event.preventDefault();
        cancelCombatPick();
    }
}

/** A receipt or late WS frame may settle a start after the initiating page stopped waiting. */
function onCombatState(event: Event): void {
    if (!active || active.options.combatId) return;
    const detail = (event as CustomEvent).detail as { encounterId?: string; inCombat?: boolean } | undefined;
    const id = String((window as Window & { ftGetActiveEncounterId?: () => unknown }).ftGetActiveEncounterId?.() ?? '');
    if (detail?.inCombat === true && detail.encounterId?.toUpperCase() === id.toUpperCase()) cancelCombatPick();
}

/** Arm pick mode. The initiative action owns the next creature click; stale ability targets are not seeds. */
export function startCombatPick(options: CombatPickOptions): void {
    if (active) cancelCombatPick();
    claimFt3dInteractionMode('initiative-pick', cancelCombatPick);
    window.ftSetTargetSelection?.('', 'initiative-pick-arm');
    active = { options, seedIds: [], participants: [], excludedIds: new Set(), knownSelection: new Set(), previewToken: 0, busy: false, loading: false, adding: !!options.combatId };
    window.addEventListener('ft:targets-changed', onTargetsChanged);
    window.addEventListener('message', onFrameMessage);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('ft:encounter-turn-state', onCombatState);
    postPickMode(true);
    ftDebugLog('combat', { phase: 'combatPickArmed' });
    render();
    if (options.initialSeedId) pickCombatCreature(options.initialSeedId);
}

export function cancelCombatPick(): void {
    if (!active) {
        releaseFt3dInteractionMode('initiative-pick');
        return;
    }
    active.pending?.abort();
    window.removeEventListener('ft:targets-changed', onTargetsChanged);
    window.removeEventListener('message', onFrameMessage);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('ft:encounter-turn-state', onCombatState);
    postPickMode(false);
    active = null;
    document.getElementById(PANEL_ID)?.remove();
    window.ftSetTargetSelection?.('', 'initiative-pick-retire');
    postTargetState(selectionIds()); // back to the plain selection ring
    releaseFt3dInteractionMode('initiative-pick');
}

async function confirmCombatPick(): Promise<void> {
    if (!active || active.busy || active.loading || active.seedIds.length === 0 || active.participants.length === 0) return;
    const pick = active;
    const controller = new AbortController();
    pick.pending = controller;
    active.busy = true;
    render();
    const seedIds = [...active.seedIds];
    const excludedIds = [...active.excludedIds];
    const options = active.options;
    try {
        const result = await withCombatProgress((signal, progress) => options.onStart(seedIds, excludedIds, signal, progress), controller.signal,
            stage => { if (active === pick) { pick.progress = stage; render(); } });
        if (active === pick && result !== null) cancelCombatPick();
    } catch (error) {
        if (active === pick) refuseCombatChange(error, { surface: 'initiative-pick', action: options.combatId ? 'join' : 'start' });
    } finally {
        if (active === pick) { delete pick.pending; pick.busy = false; render(); }
    }
}

export function combatPickIsActive(): boolean {
    return active !== null;
}

/** Combat-widget bridge. Unlike the old target-selection listener, this consumes a combatant click
 * without manufacturing persistent target state merely to identify the initiative aggressor. */
function pickCombatCreature(entityId: string): boolean {
    if (!active) return false;
    if (active.busy || active.loading) return true;
    const id = String(entityId ?? '').trim().toUpperCase();
    if (!UUID_RE.test(id) || !eligiblePick(id)) return false;
    active.knownSelection.add(id);
    if (active.adding && (active.options.combatId || active.seedIds.length > 0)) {
        active.excludedIds.delete(id);
        void preview([...new Set([...active.seedIds, id])], true);
    } else {
        void preview([id]);
    }
    return true;
}

function eligiblePick(id: string): boolean {
    const row = cachedEncounterRow(id);
    const p = cachedParticipant(id);
    return !!row && !!p
        && !existingCombatMemberIds().includes(id)
        && Number(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_dead') ?? 0) !== 1
        && (!p.private || p.item_type === 'lair');
}

function existingCombatMemberIds(): string[] {
    const combatId = active?.options.combatId;
    if (!combatId) return [];
    return (cachedEncounter()?.encounter_creatures_arr ?? []).flatMap(candidate => {
        const row = candidate as CachedEncounterRow;
        return String(ftEncounterCreatureField(row.encounter_creatures, 'combat_id') ?? '').toUpperCase() === combatId.toUpperCase()
            ? [String(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase()] : [];
    });
}

declare global {
    interface Window {
        ftStartCombatPick?: typeof startCombatPick;
        ftCancelCombatPick?: typeof cancelCombatPick;
        ftCombatPickEntity?: typeof pickCombatCreature;
    }
}
window.ftStartCombatPick = startCombatPick;
window.ftCancelCombatPick = cancelCombatPick;
window.ftCombatPickEntity = pickCombatCreature;
